import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faAward,
  faClipboardList,
  faCodeBranch,
  faCreditCard,
  faGlobe,
  faLock,
  faPhone,
  faShieldHalved,
  faUser
} from '@fortawesome/pro-regular-svg-icons';

export type TabValue =
  'basic' | 'frontier' | 'password' | 'card' | '2fa' | 'phone' | 'loyalty' | 'reporting' | 'approvers';

/** `section` = editable profile areas with Save; `accountAction` = separate flows (e.g. password reset). */
export type NavItemKind = 'section' | 'accountAction';

export type NavItemConfig = {
  value: TabValue;
  label: string;
  icon: IconDefinition;
  kind: NavItemKind;
  /** When true, nav entry is only shown in business (company) mode, not personal My Profile. */
  businessOnly?: boolean;
  /** When true, nav entry is only shown for OBT company super users. */
  requiresObtSuperUser?: boolean;
  /** When true, nav entry is only shown if company approval workflow is enabled for OBT and/or agent bookings. */
  requiresApprovalWorkflowEnabled?: boolean;
};

export const navItems: NavItemConfig[] = [
  { value: 'basic', label: 'Basic Details', icon: faUser, kind: 'section' },
  { value: 'frontier', label: 'Frontier Online', icon: faGlobe, kind: 'section' },
  { value: 'password', label: 'Reset Password', icon: faLock, kind: 'accountAction' },
  { value: 'card', label: 'Card on File', icon: faCreditCard, kind: 'section' },
  { value: '2fa', label: 'Two Factor Auth', icon: faShieldHalved, kind: 'section' },
  { value: 'phone', label: 'Phone Numbers', icon: faPhone, kind: 'section' },
  { value: 'loyalty', label: 'Loyalty Numbers', icon: faAward, kind: 'section' },
  {
    value: 'reporting',
    label: 'Reporting Reqs.',
    icon: faClipboardList,
    kind: 'section',
    businessOnly: true
  },
  {
    value: 'approvers',
    label: 'Approvers',
    icon: faCodeBranch,
    kind: 'section',
    businessOnly: true,
    requiresObtSuperUser: true,
    requiresApprovalWorkflowEnabled: true
  }
];

export const navSectionItems = navItems.filter((i) => i.kind === 'section');
export const navAccountItems = navItems.filter((i) => i.kind === 'accountAction');

/**
 * Key used on react-router `location.state` to request an initial profile section
 * when navigating into the refactored profile page (one-time, ephemeral signal).
 */
export const profileInitialSectionStateKey = 'initialProfileSection' as const;

/**
 * Returns a valid TabValue if `state` carries a known section under
 * `profileInitialSectionStateKey`, otherwise null. Defensive against
 * arbitrary location state shapes.
 */
export function parseInitialSectionFromLocationState(state: unknown): TabValue | null {
  if (!state || typeof state !== 'object') return null;
  const value = (state as Record<string, unknown>)[profileInitialSectionStateKey];
  if (typeof value !== 'string') return null;
  const found = navItems.find((i) => i.value === value);
  return found ? found.value : null;
}

export const navSectionSwitchLockTooltip =
  "The current section you're editing has unsaved changes. Use Save or Cancel before switching to another section.";

export const navSelectTravelerTooltip = 'Select a traveler to edit profile sections.';
